import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiJson, getToken } from "@/api/client.js";
import { PWSGroupMap } from "@/components/PWSGroupMap.jsx";
import { PWSWorkModal } from "@/components/PWSWorkModal.jsx";
import { TomorrowDesk } from "@/components/TomorrowDesk.jsx";
import { MAP_CHOICES } from "@/content/sepaqSheet.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { nextSpotNumber, withSpotNumbers } from "@/lib/spotNumbers.js";

const IOS = "https://apps.apple.com/ca/app/nordquest/id6759458166";
const ANDROID = "https://play.google.com/store/apps/details?id=ca.pulsarinformatique.nordquest";

const FALLBACK_POINTS = [
  { id: "pt_nq_01", name: "Beau spot Eric love", latitude: 47.3137007328745, longitude: -72.4724914382474 },
  { id: "pt_nq_02", name: "Belle cache très longue shot facile d’accès", latitude: 47.2815067835463, longitude: -72.4565321158924 },
  { id: "pt_nq_03", name: "Buchage à blanc", latitude: 47.3215522570358, longitude: -72.485280941468 },
  { id: "pt_nq_04", name: "Cache à dan", latitude: 47.2863311244563, longitude: -72.4827378803693 },
  { id: "pt_nq_05", name: "Caméra du bout", latitude: 47.3115561906089, longitude: -72.4953218672105 },
  { id: "pt_nq_06", name: "Chalet neuf pas de piste", latitude: 47.3048143178284, longitude: -72.4774955894974 },
  { id: "pt_nq_07", name: "Corde et caméra", latitude: 47.313286307504, longitude: -72.4486079941272 },
  { id: "pt_nq_08", name: "Le vieux chack", latitude: 47.2904235797331, longitude: -72.4795765397346 },
  { id: "pt_nq_09", name: "Trail 4 roues", latitude: 47.2730379383495, longitude: -72.4552695389762 },
];

function placePoints(rows) {
  const known = Object.fromEntries(FALLBACK_POINTS.map((point) => [point.id, point]));
  return rows.map((point) => {
    const base = known[point.id];
    if (base) {
      return {
        ...point,
        name: base.name,
        latitude: base.latitude,
        longitude: base.longitude,
        author: point.author || "Éric",
        created_at: point.created_at || "2026-08-29",
      };
    }
    return {
      ...point,
      latitude: Number(point.latitude ?? point.lat),
      longitude: Number(point.longitude ?? point.lng),
    };
  });
}

function frenchDate(stamp) {
  if (!stamp) return "";
  const raw = String(stamp);
  const date = new Date(raw.length <= 10 ? `${raw.slice(0, 10)}T12:00:00` : raw);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto", day: "numeric", month: "long", year: "numeric",
  }).format(date);
}

function pointStatus(point, today) {
  if (String(point.id || "").startsWith("pt_nq_")) return "Carte du groupe V1";
  if (point.pending && torontoDay(point.created_at) === today) return "NOUVEAU AUJOURD’HUI";
  if (point.pending) return "En attente pour la prochaine carte du groupe";
  return "Carte du groupe";
}

const HUNT_DAYS = {
  "2026-10-02": "Ven 2 oct. — Arrivée",
  "2026-10-03": "Sam 3 oct. — Jour 1",
  "2026-10-04": "Dim 4 oct. — Jour 2",
  "2026-10-05": "Lun 5 oct. — Jour 3",
  "2026-10-06": "Mar 6 oct. — Jour 4",
  "2026-10-07": "Mer 7 oct. — Jour 5",
  "2026-10-08": "Jeu 8 oct. — Jour 6",
  "2026-10-09": "Ven 9 oct. — Dernier jour",
  "2026-10-10": "Sam 10 oct. — Retour",
};

function huntDayLabel(day) {
  return HUNT_DAYS[day] || "Avant la chasse";
}

const REPAIR_STEPS = [
  "Ouvrir NordQuest",
  "Retirer l’ancienne série de points du groupe",
  "Revenir dans Orignal2026",
  "Télécharger la carte complète actuelle",
  "Ouvrir NordQuest",
  "Importer la nouvelle carte complète",
  "Vérifier le nombre de points",
  "Revenir dans Orignal2026",
];

function torontoDay(stamp = new Date()) {
  const date = stamp instanceof Date ? stamp : new Date(stamp);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(date);
}

function usePhone() {
  const [phone, setPhone] = useState(() => typeof window.matchMedia === "function" && window.matchMedia("(max-width: 899px)").matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 899px)");
    const onChange = () => setPhone(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return phone;
}

function spotFilters(hasNext) {
  const rows = [
    ["all", "Tous"],
    ["v1", "Carte actuelle V1"],
    ["today", "Nouveaux ce soir"],
    ["check", "À vérifier"],
  ];
  if (hasNext) rows.push(["next", "Nouvelle version"]);
  return rows;
}

function localClock(date = new Date()) {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto", day: "numeric", month: "long", hour: "numeric", minute: "2-digit",
  }).format(date);
}

export function GroupBoard({ onAssign = null }) {
  const phone = usePhone();
  const { name: hunterName } = useAuth();
  const { actingAsAdmin } = useMarcelMode();
  const [points, setPoints] = useState(FALLBACK_POINTS);
  const [picked, setPicked] = useState(null);
  const [filter, setFilter] = useState("all");
  const [layer, setLayer] = useState("official");
  const [listOpen, setListOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState(false);
  const [pastSource, setPastSource] = useState(false);
  const [pin, setPin] = useState(null);
  const [pointName, setPointName] = useState("");
  const [pointNote, setPointNote] = useState("");
  const [group, setGroup] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [flow, setFlow] = useState("");
  const [notice, setNotice] = useState("");
  const [menuPoint, setMenuPoint] = useState(null);
  const [dropPoint, setDropPoint] = useState(null);
  const [historyPoint, setHistoryPoint] = useState(null);
  const [futureNote, setFutureNote] = useState("");
  const [retired, setRetired] = useState([]);
  const mapRef = useRef(null);
  const listRef = useRef(null);
  const fitted = useRef(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const onZoomLimit = useCallback((past) => setPastSource(past), []);
  const hasNext = Boolean(group?.latest?.code && group.latest.code !== "1");

  useEffect(() => {
    api("/api/terrain-points").then((body) => {
      if (body?.items?.length) {
        setPoints(placePoints(body.items));
        setRetired(body.retired_numbers || []);
      }
      else return api("/api/nordquest").then((fallback) => { if (fallback?.points?.length) setPoints(placePoints(fallback.points)); });
    }).catch(() => {
      api("/api/nordquest").then((body) => { if (body?.points?.length) setPoints(placePoints(body.points)); }).catch(() => {});
    });
    api("/api/nordquest/group").then((body) => {
      if (body && Array.isArray(body.hunters)) setGroup(body);
    }).catch(() => {});
  }, []);

  const listed = useMemo(() => withSpotNumbers(points).map((point) => {
    const latitude = Number(point.latitude ?? point.lat);
    const longitude = Number(point.longitude ?? point.lng);
    return {
      ...point,
      latitude,
      longitude,
      fresh: Boolean(point.pending && torontoDay(point.created_at) === torontoDay()),
    };
  }).sort((a, b) => a.number - b.number), [points]);

  const today = torontoDay();
  const visible = listed.filter((point) => {
    if (filter === "v1") return String(point.id).startsWith("pt_nq_");
    if (filter === "today") return point.pending && torontoDay(point.created_at) === today;
    if (filter === "check") return point.status === "À vérifier par Marcel" || point.review === "check";
    if (filter === "next") return Boolean(point.pending || point.published_in);
    return true;
  });

  useEffect(() => {
    if (fitted.current || !visible.length) return undefined;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const ok = mapRef.current?.fitPoints?.(visible);
      if (ok) {
        fitted.current = true;
        window.clearInterval(timer);
      } else if (tries > 25) window.clearInterval(timer);
    }, 200);
    return () => window.clearInterval(timer);
  }, [visible]);

  function focusPoint(point) {
    if (!point) return;
    setPicked(point);
    if (phone) setListOpen(true);
    const row = listRef.current?.querySelector(`[data-testid="spot-${point.id}"]`);
    if (typeof row?.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
    if (Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) {
      mapRef.current?.focus?.(point.latitude, point.longitude);
      window.setTimeout(() => setAnchor(mapRef.current?.anchor?.(point.latitude, point.longitude) || null), 480);
    }
  }

  function clearPick() {
    setSheetOpen(false);
    setPicked(null);
    setAnchor(null);
  }

  function choose(point) {
    focusPoint(point);
    setSheetOpen(true);
    if (onAssign) onAssign(point);
  }

  function cancelPoint() {
    setPin(null);
    setPointName("");
    setPointNote("");
    setAdding(false);
    setMoving(false);
    setEditingId("");
  }

  async function reloadBoard() {
    const body = await api("/api/terrain-points");
    if (body?.items?.length) {
      setPoints(placePoints(body.items));
      setRetired(body.retired_numbers || []);
    }
    const state = await api("/api/nordquest/group");
    if (state?.hunters) setGroup(state);
  }

  async function savePoint() {
    if (!pin || !pointName.trim()) return;
    if (editingId) {
      const saved = await apiJson(`/api/terrain-points/${editingId}`, {
        name: pointName.trim(),
        note: pointNote.trim(),
        latitude: pin.latitude,
        longitude: pin.longitude,
      }, "PATCH");
      setPoints((current) => current.map((row) => (row.id === editingId ? { ...row, ...saved, latitude: pin.latitude, longitude: pin.longitude, name: pointName.trim(), note: pointNote.trim() } : row)));
      setSheetOpen(true);
      cancelPoint();
      return;
    }
    const number = nextSpotNumber(points, retired);
    const saved = await apiJson("/api/terrain-points", {
      name: pointName.trim(),
      note: pointNote.trim(),
      latitude: pin.latitude,
      longitude: pin.longitude,
      number,
    }, "POST");
    const created = saved?.id ? { ...saved, number: saved.number || number } : {
      id: `pt_local_${Date.now()}`,
      name: pointName.trim(),
      note: pointNote.trim(),
      latitude: pin.latitude,
      longitude: pin.longitude,
      number,
      pending: true,
      queued: Boolean(saved?._offline),
      author: "Moi",
      created_at: new Date().toISOString(),
    };
    setPoints((current) => [...current, created]);
    setPicked(created);
    setSheetOpen(true);
    cancelPoint();
    reloadBoard();
  }

  async function confirmMap(result) {
    if (!group?.latest?.code) return;
    await apiJson("/api/nordquest/group/installed", { code: group.latest.code, result }, "POST");
    const body = await api("/api/nordquest/group");
    if (body?.hunters) setGroup(body);
    setFlow("");
    if (result === "ok") setNotice("Mise à jour réussie ✓");
    else if (result === "installed") setNotice("Carte installée ✓");
    else if (result === "repaired") setNotice("Réparation terminée ✓");
  }

  async function reportMissing() {
    if (!group?.latest?.code) return;
    await apiJson("/api/nordquest/group/installed", { code: group.latest.code, result: "missing" }, "POST");
    const body = await api("/api/nordquest/group");
    if (body?.hunters) setGroup(body);
    setNotice("");
    setFlow("repair");
  }

  const card = listed.find((point) => point.id === picked?.id) || picked;
  const received = listed.filter((point) => point.pending && torontoDay(point.created_at) === today);
  const latest = group?.latest;
  const offer = group?.mine_offer || "complete";
  const expectedCount = group?.mine_expected || latest?.point_count || 9;
  const deltaCount = group?.mine_delta_count || 0;
  const nordquest = typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "") ? ANDROID : IOS;
  return (
    <div className={`og-groupboard${phone ? " is-phone" : ""}`} data-testid="group-board" data-layer={layer}>
      <div className="og-groupboard__bar">
        <button type="button" className="og-btn og-btn--primary" data-testid="open-nordquest" onClick={() => window.open(nordquest, "_blank", "noopener")}>Ouvrir dans NordQuest</button>
        <button type="button" className={`og-btn${adding ? " is-on" : ""}`} data-testid="add-point" onClick={() => { setAdding(true); setPin(null); setEditingId(""); setMoving(false); setSheetOpen(false); }}>+ Ajouter un point</button>
        {phone ? <button type="button" className="og-btn" data-testid="toggle-points" onClick={() => setListOpen((open) => !open)}>Points</button> : null}
        {phone ? <button type="button" className={`og-btn${layersOpen ? " is-on" : ""}`} data-testid="map-layers-toggle" onClick={() => setLayersOpen((open) => !open)}>Couches</button> : null}
        {phone ? <button type="button" className={`og-btn${filter === "today" ? " is-on" : ""}`} data-testid="spot-filter-today-phone" onClick={() => { setFilter((current) => (current === "today" ? "all" : "today")); setListOpen(true); }}>Nouveaux</button> : null}
        {!phone || layersOpen ? <span className="og-groupboard__filters" data-testid="map-layers">
          {MAP_CHOICES.map((choice) => (
            <button key={choice.id} type="button" className={`og-btn${layer === choice.id ? " is-on" : ""}`} data-testid={`layer-${choice.id}`} onClick={() => setLayer(choice.id)}>{choice.label}</button>
          ))}
        </span> : null}
      </div>
      <p className="og-groupboard__note" data-testid="map-caption">
        {layer === "official"
          ? (phone
            ? `Carte officielle Sépaq.${pastSource ? " Détail maximum du fichier atteint." : ""}`
            : `Carte officielle Sépaq · 4 275 × 3 600 px. La navigation reste dans NordQuest.${pastSource ? " Détail maximum du fichier atteint." : ""}`)
          : layer === "satellite"
            ? "Satellite. Ce n’est pas la carte Sépaq."
            : layer === "topo"
              ? "Relief. Ce n’est pas la carte Sépaq."
              : "Carte simple. Ce n’est pas la carte Sépaq."}
      </p>
      {!phone ? <div className="og-groupboard__filters" data-testid="spot-filters">
        {spotFilters(hasNext).map(([id, label]) => (
          <button key={id} type="button" className={`og-btn${filter === id ? " is-on" : ""}`} data-testid={`spot-filter-${id}`} onClick={() => setFilter(id)}>{label}</button>
        ))}
      </div> : null}
      <div className="og-groupboard__stage">
        {!phone || listOpen ? (
          <ul className="og-spotlist" data-testid="spot-list" ref={listRef}>
            {phone ? (
              <li className="og-groupboard__filters" data-testid="spot-filters">
                {spotFilters(hasNext).map(([id, label]) => (
                  <button key={id} type="button" className={`og-btn${filter === id ? " is-on" : ""}`} data-testid={`spot-filter-${id}`} onClick={() => setFilter(id)}>{label}</button>
                ))}
              </li>
            ) : null}
            {visible.map((point) => (
              <li key={point.id} className="og-spotrow">
                <button type="button" className={`og-btn og-spotline${picked?.id === point.id ? " is-on" : ""}`} data-testid={`spot-${point.id}`} data-number={point.number} data-lat={point.latitude} data-lng={point.longitude} onClick={() => choose(point)}>
                  <span className="og-spotno">#{point.number}</span>
                  <strong>{point.name}</strong>
                  <span className="og-spotloc" aria-hidden="true" />
                  {point.queued ? <span className="og-spotstat">En attente de synchronisation</span> : null}
                  {point.pending ? <span className="og-spotstat">NOUVEAU AUJOURD’HUI · En attente pour la prochaine carte du groupe</span> : null}
                  {point.aside ? <span className="og-spotstat">Retiré de la carte de demain</span> : null}
                </button>
                {point.pending || actingAsAdmin ? (
                  <button type="button" className="og-btn" data-testid={`spot-menu-${point.id}`} aria-label={`Actions du point ${point.number}`} onClick={() => { setFutureNote(point.future_note || ""); setMenuPoint(point); }}>⋯</button>
                ) : null}
              </li>
            ))}
            <li data-testid="received-today">
              <strong>Points reçus aujourd’hui : {received.length}</strong>
              {received.map((point) => <span key={point.id}>#{point.number} {point.author || ""}</span>)}
            </li>
          </ul>
        ) : null}
        <div className="og-groupboard__map" data-testid="spot-map" data-basemap={layer}>
          <PWSGroupMap
            ref={mapRef}
            basemap={layer}
            points={visible.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude)).map((point) => ({ ...point, fresh: point.fresh }))}
            selectedId={picked?.id}
            onSelect={choose}
            onBlank={clearPick}
            placeMode={(adding && !pin) || moving}
            draftPin={pin}
            onZoomLimit={onZoomLimit}
            onAddAt={(position) => { setPin(position); setAdding(false); setMoving(false); }}
            testId="spot-territory-map"
          />
          {(adding && !pin) || moving ? (
            <div className="og-placehint" data-testid="place-hint">
              <p>{moving ? "Touchez le nouvel endroit du point." : "Touchez l’endroit sur la carte."}</p>
              <button type="button" className="og-btn" data-testid="place-cancel" onClick={cancelPoint}>Annuler</button>
            </div>
          ) : null}
          <div className="og-mapchrome" aria-hidden="true">
            <span className="og-compass">N</span>
            <span className="og-sepaqbadge">Sépaq</span>
          </div>
          {card && sheetOpen && !pin ? (
            <aside
              className={`og-spotfloat${anchor ? " is-placed" : ""}${anchor && anchor.width && anchor.x > anchor.width * 0.62 ? " is-left" : ""}`}
              data-testid="spot-sheet"
              style={anchor ? { left: anchor.x, top: anchor.y } : undefined}
              onClick={(event) => event.stopPropagation()}
            >
              <button type="button" className="og-spotfloat__x" data-testid="spot-sheet-close" onClick={() => setSheetOpen(false)} aria-label="Fermer la fiche">×</button>
              <article data-testid="spot-card">
                <h3>#{card.number} {card.name}</h3>
                <p>{card.author || "À confirmer"}</p>
                <p>{frenchDate(card.created_at) || "À confirmer"}</p>
                <p>{pointStatus(card, today)}</p>
                {card.note ? <p>{card.note}</p> : null}
                <p data-testid="spot-coords">{Number(card.latitude).toFixed(5)}, {Number(card.longitude).toFixed(5)}</p>
              </article>
            </aside>
          ) : null}
        </div>
      </div>
      {onAssign && card ? (
        <button type="button" className="og-btn og-btn--primary" data-testid="assign-spot" onClick={() => onAssign(card)}>Affecter ce spot au véhicule</button>
      ) : null}
      <div data-testid="map-distribution" data-offer={offer}>
        {notice ? <p data-testid="map-notice">{notice}{latest ? ` Version confirmée : V${latest.code}. Points attendus : ${expectedCount}.` : ""}</p> : null}
        {offer === "current" && latest ? (
          <div data-testid="map-current">
            <p>Carte à jour ✓</p>
            <p>Version confirmée : V{latest.code}</p>
            <p>Points attendus : {expectedCount}</p>
            <button type="button" className="og-btn" data-testid="map-missing" onClick={reportMissing}>Il me manque quelque chose</button>
          </div>
        ) : null}
        {offer === "delta" && latest ? (
          <div data-testid="map-update">
            <p>Mise à jour disponible</p>
            <p>V{latest.code} devrait contenir {expectedCount} points</p>
            <button type="button" className="og-btn og-btn--primary" data-testid="map-update-download" onClick={async () => {
              const ok = await downloadPack(`/api/nordquest/group/pack/${latest.code}/maj/${group.mine_from || "1"}`);
              if (ok) setFlow("check");
              else setFlow("repair");
            }}>{deltaCount === 1 ? "Ajouter le nouveau point" : `Ajouter les ${deltaCount} nouveaux points`}</button>
            <button type="button" className="og-btn" data-testid="map-repair" onClick={() => setFlow("repair")}>Réparer ma carte</button>
          </div>
        ) : null}
        {offer === "repair" ? (
          <div data-testid="map-repair-offer">
            <p>Réparer ma carte</p>
            <p>Réinstaller une copie complète et propre de la dernière carte du groupe.</p>
            <button type="button" className="og-btn og-btn--primary" data-testid="map-repair" onClick={() => setFlow("repair")}>Réparer ma carte</button>
          </div>
        ) : null}
        {offer === "complete" ? (
          <button type="button" className="og-btn og-install" data-testid="install-current" onClick={() => setFlow("install")}>{!latest || latest.code === "1" ? "Installer la carte actuelle" : `Installer la carte complète V${latest.code}`}</button>
        ) : null}
        {latest && latest.code !== "1" && (group?.expected_points || []).length ? (
          <details data-testid="expected-points">
            <summary>Points attendus dans V{latest.code}</summary>
            <ol>
              {group.expected_points.map((point) => <li key={`${point.number}-${point.name}`}>#{point.number} — {point.name}</li>)}
            </ol>
          </details>
        ) : null}
      </div>
      {menuPoint ? (
        <PWSWorkModal stacked title={`#${menuPoint.number} — ${menuPoint.name}`} onClose={() => setMenuPoint(null)} testId="point-menu" closeTestId="point-menu-close">
          {menuPoint.pending ? (
            <div className="og-now__actions">
              <button type="button" className="og-btn" data-testid="menu-rename" onClick={() => { setEditingId(menuPoint.id); setPin({ latitude: menuPoint.latitude, longitude: menuPoint.longitude }); setPointName(menuPoint.name || ""); setPointNote(menuPoint.note || ""); setMenuPoint(null); }}>Modifier le nom</button>
              <button type="button" className="og-btn" data-testid="menu-note" onClick={() => { setEditingId(menuPoint.id); setPin({ latitude: menuPoint.latitude, longitude: menuPoint.longitude }); setPointName(menuPoint.name || ""); setPointNote(menuPoint.note || ""); setMenuPoint(null); }}>Modifier la description</button>
              <button type="button" className="og-btn" data-testid="menu-move" onClick={() => { setPicked(menuPoint); setEditingId(menuPoint.id); setPin({ latitude: menuPoint.latitude, longitude: menuPoint.longitude }); setPointName(menuPoint.name || ""); setPointNote(menuPoint.note || ""); setMoving(true); setMenuPoint(null); setSheetOpen(false); }}>Déplacer</button>
              <button type="button" className="og-btn" data-testid="menu-aside" onClick={async () => { await apiJson(`/api/terrain-points/${menuPoint.id}`, { aside: !menuPoint.aside }, "PATCH"); setMenuPoint(null); reloadBoard(); }}>{menuPoint.aside ? "Remettre dans la carte de demain" : "Retirer de la carte de demain"}</button>
              <button type="button" className="og-btn" data-testid="menu-delete" onClick={() => { setDropPoint(menuPoint); setMenuPoint(null); }}>Supprimer définitivement</button>
            </div>
          ) : (
            <div className="og-now__actions">
              <label>Description pour la prochaine carte
                <textarea className="og-input" data-testid="future-note" value={futureNote} onChange={(event) => setFutureNote(event.target.value)} rows={3} />
              </label>
              <button type="button" className="og-btn" data-testid="menu-future-note" onClick={async () => { await apiJson(`/api/terrain-points/${menuPoint.id}/future`, { action: "note", note: futureNote }, "POST"); setMenuPoint(null); reloadBoard(); }}>Modifier la description pour la version future</button>
              <button type="button" className="og-btn" data-testid="menu-remove-next" onClick={async () => { await apiJson(`/api/terrain-points/${menuPoint.id}/future`, { action: "remove_next" }, "POST"); setMenuPoint(null); reloadBoard(); }}>Retirer de la prochaine version</button>
              <button type="button" className="og-btn" data-testid="menu-obsolete" onClick={async () => { await apiJson(`/api/terrain-points/${menuPoint.id}/future`, { action: "obsolete" }, "POST"); setMenuPoint(null); reloadBoard(); }}>Marquer comme obsolète</button>
              <button type="button" className="og-btn" data-testid="menu-history" onClick={() => { setHistoryPoint(menuPoint); setMenuPoint(null); }}>Voir historique</button>
            </div>
          )}
        </PWSWorkModal>
      ) : null}
      {dropPoint ? (
        <PWSWorkModal stacked title="Supprimer ce point définitivement ?" onClose={() => setDropPoint(null)} testId="point-delete" closeTestId="point-delete-close">
          <p>Il n’a jamais été publié dans une carte du groupe.</p>
          <div className="og-now__actions">
            <button type="button" className="og-btn" data-testid="point-delete-cancel" onClick={() => setDropPoint(null)}>Annuler</button>
            <button type="button" className="og-btn og-btn--primary" data-testid="point-delete-confirm" onClick={async () => {
              await api(`/api/terrain-points/${dropPoint.id}`, { method: "DELETE" });
              setPoints((current) => current.filter((row) => row.id !== dropPoint.id));
              setRetired((current) => [...current, dropPoint.number]);
              setPicked(null);
              setSheetOpen(false);
              setDropPoint(null);
              reloadBoard();
            }}>Supprimer</button>
          </div>
        </PWSWorkModal>
      ) : null}
      {historyPoint ? (
        <PWSWorkModal stacked title={`Historique #${historyPoint.number}`} onClose={() => setHistoryPoint(null)} testId="point-history" closeTestId="point-history-close">
          <p>La version déjà publiée reste inchangée.</p>
          {(historyPoint.history || []).length ? (
            <ul>{historyPoint.history.map((row) => <li key={`${row.at}-${row.action}`}>{row.at} · {row.detail || row.action}</li>)}</ul>
          ) : <p>Aucun changement pour une prochaine version.</p>}
        </PWSWorkModal>
      ) : null}
      {actingAsAdmin ? <TomorrowDesk
        group={group}
        points={listed}
        today={today}
        onReload={reloadBoard}
        onFocus={(point) => {
          const match = listed.find((row) => row.id === point.point_id) || listed.find((row) => row.name === point.name);
          if (match) {
            focusPoint(match);
            setSheetOpen(true);
          }
        }}
        onMove={(point) => {
          const match = listed.find((row) => row.id === point.point_id) || listed.find((row) => row.name === point.name);
          if (!match) return;
          setPicked(match);
          setPin({ latitude: match.latitude, longitude: match.longitude });
          setPointName(match.name || "");
          setPointNote(match.note || "");
          setEditingId(match.id);
          setMoving(true);
        }}
      /> : null}
      {pin && !moving ? (
        <PWSWorkModal stacked title={`#${editingId ? (listed.find((row) => row.id === editingId)?.number || "") : nextSpotNumber(points)} — Nouveau point`} onClose={cancelPoint} testId="point-modal" closeTestId="point-modal-close">
          <form className="og-form og-pointform" data-testid="point-form" onSubmit={(event) => { event.preventDefault(); savePoint(); }}>
            <p data-testid="point-number">#{editingId ? (listed.find((row) => row.id === editingId)?.number || "") : nextSpotNumber(points)} — Nouveau point</p>
            <label>Nom
              <input className="og-input" data-testid="point-name" value={pointName} onChange={(event) => setPointName(event.target.value)} required />
            </label>
            <label>Description
              <textarea className="og-input" data-testid="point-note" value={pointNote} onChange={(event) => setPointNote(event.target.value)} rows={3} />
            </label>
            <p data-testid="point-meta">{hunterName || "Chasseur"} · {localClock()} · {Number(pin.latitude).toFixed(5)}, {Number(pin.longitude).toFixed(5)}</p>
            <p data-testid="point-day">Journée : {huntDayLabel(today)}</p>
            <div className="og-now__actions">
              <button type="submit" className="og-btn og-btn--primary" data-testid="point-save" disabled={!pointName.trim()}>Enregistrer</button>
              <button type="button" className="og-btn" data-testid="point-move" onClick={() => setMoving(true)}>Déplacer le point</button>
              <button type="button" className="og-btn" data-testid="point-cancel" onClick={cancelPoint}>Annuler</button>
            </div>
          </form>
        </PWSWorkModal>
      ) : null}
      {flow === "check" && latest ? (
        <PWSWorkModal stacked title="Vérifier les points" onClose={() => setFlow("")} testId="map-check" closeTestId="map-check-close">
          <p data-testid="map-check-count">V{latest.code} devrait maintenant contenir {expectedCount} points</p>
          <div className="og-now__actions">
            <button type="button" className="og-btn og-btn--primary" data-testid="map-seen" onClick={() => confirmMap("ok")}>Je vois mes {expectedCount} points ✓</button>
            <button type="button" className="og-btn" data-testid="map-missing" onClick={reportMissing}>Il me manque quelque chose</button>
          </div>
        </PWSWorkModal>
      ) : null}
      {(flow === "repair" || flow === "install") && latest ? (
        <PWSWorkModal stacked title={flow === "repair" ? "Réparer ma carte" : "Installer la carte complète"} onClose={() => setFlow("")} testId="repair-guide" closeTestId="repair-guide-close">
          <p>Réinstaller une copie complète et propre de la dernière carte du groupe.</p>
          <ol data-testid="repair-steps">
            {REPAIR_STEPS.map((line, index) => <li key={`${index}-${line}`}>{line}</li>)}
          </ol>
          <div className="og-now__actions">
            <button type="button" className="og-btn og-btn--primary" data-testid="repair-download" onClick={() => downloadPack(`/api/nordquest/group/pack/${latest.code}/complet`)}>Télécharger la carte complète actuelle</button>
            <button type="button" className="og-btn" data-testid="repair-done" onClick={() => confirmMap(flow === "repair" ? "repaired" : "installed")}>{flow === "repair" ? "Ma carte est réparée ✓" : "Ma carte est installée ✓"}</button>
          </div>
          {(group?.expected_points || []).length ? (
            <details data-testid="repair-checklist">
              <summary>Points attendus dans V{latest.code}</summary>
              <ol>
                {group.expected_points.map((point) => <li key={`${point.number}-${point.name}`}>#{point.number} — {point.name}</li>)}
              </ol>
            </details>
          ) : null}
          <details>
            <summary>Options avancées</summary>
            <button type="button" className="og-btn" data-testid="advanced-gpx" onClick={() => downloadPack(`/api/nordquest/group/file/${latest.code}/gpx`)}>Télécharger le GPX</button>
          </details>
        </PWSWorkModal>
      ) : null}
    </div>
  );
}

async function downloadPack(path) {
  const token = getToken();
  const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) return false;
  const blob = await res.blob();
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = match?.[1] || "carte-du-groupe.kml";
  link.click();
  URL.revokeObjectURL(link.href);
  return true;
}
